/**
 * FindingsPage — global validation findings list at /findings.
 *
 * Default filter: open findings on the latest run per loan file. Users
 * can filter by UC, severity, status, and verdictSource. Every row
 * links to the finding-detail page.
 *
 * Auto-refresh every 10s so new findings from ongoing runs appear.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, CheckCheck } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { validationApi, type FindingsListQuery } from '@/api/validation-api';
import { ingestionApi } from '@/api/ingestion-api';
import { usePermission } from '@/auth/use-permission';
import { FINDING_STATUS, VALIDATION_RESOLVE, VERDICT_SOURCE, type Severity } from '@loanaudit/shared';
import { VerdictSourceBadge } from './FindingsPanel';

// -------------------------------------------------------------------------

const SEVERITY_TONE: Record<Severity, 'danger' | 'warning' | 'info' | 'muted'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'info',
  info: 'muted',
};

// Human-readable labels — underlying enum values stay for logic.
const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info',
};
const STATUS_LABEL: Record<string, string> = {
  open: 'Open', accepted: 'Accepted', waived: 'Waived',
  false_positive: 'False positive', superseded: 'Superseded',
};

export function FindingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canResolve = has(VALIDATION_RESOLVE);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState<string>(() => searchParams.get('sortBy') ?? 'severity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => (searchParams.get('sortDir') === 'desc' ? 'desc' : 'asc'),
  );

  const [filters, setFilters] = useState<FindingsListQuery>(() => {
    const s = searchParams.get('status');
    const latest = searchParams.get('latestOnly');
    return {
      page: Number(searchParams.get('page')) || 1,
      pageSize: 25,
      loanFileId: searchParams.get('loanFileId') || undefined,
      ucId: (searchParams.get('ucId') || undefined) as FindingsListQuery['ucId'],
      severity: (searchParams.get('severity') || undefined) as FindingsListQuery['severity'],
      // status absent → default Open; empty string → explicit "All non-superseded".
      status: (s === null ? FINDING_STATUS.OPEN : s || undefined) as FindingsListQuery['status'],
      verdictSource: (searchParams.get('verdictSource') || undefined) as FindingsListQuery['verdictSource'],
      latestOnly: latest === null ? true : latest === 'true',
    };
  });

  // Sync filter + sort state to the URL so refresh / deep-link / back preserve the view.
  useEffect(() => {
    const p = new URLSearchParams();
    if (filters.loanFileId) p.set('loanFileId', filters.loanFileId);
    if (filters.ucId) p.set('ucId', filters.ucId);
    if (filters.severity) p.set('severity', filters.severity);
    p.set('status', filters.status ?? ''); // '' encodes "All non-superseded"
    if (filters.verdictSource) p.set('verdictSource', filters.verdictSource);
    p.set('latestOnly', String(filters.latestOnly ?? true));
    if ((filters.page ?? 1) > 1) p.set('page', String(filters.page));
    p.set('sortBy', sortBy);
    p.set('sortDir', sortDir);
    setSearchParams(p, { replace: true });
  }, [filters, sortBy, sortDir, setSearchParams]);

  const query = useQuery({
    // Server-side sort: sortBy/sortDir go to the backend and into the key so it refetches on sort change.
    queryKey: ['validation', 'findings', filters, sortBy, sortDir],
    queryFn: () =>
      // groupByLoan makes the server sort on loan number first, so the table's
      // per-page grouping never splits a loan file across two groups and the
      // chosen sort key orders findings within each loan.
      validationApi.listFindings({
        ...filters,
        sortBy,
        sortDir,
        groupByLoan: true,
      } as unknown as FindingsListQuery),
    refetchInterval: 10_000,
  });

  // Loan numbers for the dropdown — scope findings to one loan file.
  const loanFilesQuery = useQuery({
    queryKey: ['findings', 'loanFileOptions'],
    queryFn: () => ingestionApi.list({ page: 1, pageSize: 200 }),
    staleTime: 60_000,
  });
  const loanOptions = [
    { value: '', label: 'All loan numbers' },
    ...(loanFilesQuery.data?.items ?? []).map((f) => ({ value: f.id, label: f.loanNumber })),
  ];

  const update = (patch: Partial<FindingsListQuery>) =>
    setFilters((f) => ({ ...f, ...patch, page: 1 }));

  const data = query.data;

  // Bulk-resolve all open findings (scoped to the selected loan number, if any).
  const resolveAll = useMutation({
    mutationFn: () => validationApi.resolveAllFindings({ loanFileId: filters.loanFileId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'findings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-findings'] });
    },
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const resolveScope = filters.loanFileId
    ? 'all open findings for the selected loan number'
    : 'ALL open findings across every loan file';

  const columns: Column<any>[] = [
    {
      key: 'severity', header: 'Severity', sortable: true, width: '7rem',
      cell: (f) => <Badge tone={SEVERITY_TONE[f.severity as Severity]}>{SEVERITY_LABEL[f.severity] ?? f.severity}</Badge>,
    },
    {
      key: 'finding', header: 'Finding', sortable: true,
      cell: (f) => (
        <div className="min-w-0">
          <div className="font-medium text-text-primary truncate">{f.title}</div>
          <div className="text-text-muted line-clamp-1">{f.description}</div>
        </div>
      ),
    },
    // No Loan column: the loan number is the group header, so repeating it on
    // every row is noise. Groups are ordered by loan number ascending.
    {
      key: 'rule', header: 'UC · Rule', width: '11rem',
      cell: (f) => (
        <div>
          <div className="uppercase font-semibold text-text-muted">{f.ucId}</div>
          <div className="font-mono text-text-tertiary">{f.ruleId}</div>
        </div>
      ),
    },
    {
      key: 'path', header: 'Path', width: '9rem',
      cell: (f) => <VerdictSourceBadge source={f.verdictSource} />,
    },
    {
      key: 'status', header: 'Status', sortable: true, width: '9rem',
      cell: (f) => (
        <Badge
          tone={
            f.status === FINDING_STATUS.OPEN
              ? 'warning'
              : f.status === FINDING_STATUS.FALSE_POSITIVE
                ? 'muted'
                : 'success'
          }
        >
          {STATUS_LABEL[f.status] ?? f.status}
        </Badge>
      ),
    },
  ];

  // Sort is now server-side; toggling just updates state → refetch (see queryKey).
  const handleSort = (key: string) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  return (
    <AppShell title="Findings">
      <div className="space-y-4">
        <PageHeader
          title="Validation Findings"
          subtitle="Open findings grouped by loan file (latest runs)."
          actions={
            canResolve ? (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<CheckCheck className="w-3.5 h-3.5" />}
                onClick={() => setConfirmOpen(true)}
                loading={resolveAll.isPending}
                disabled={(data?.total ?? 0) === 0}
              >
                Resolve all
              </Button>
            ) : undefined
          }
        />

        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-text-muted" />
            <SectionLabel>Filters</SectionLabel>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Select
              label="Loan number"
              value={filters.loanFileId ?? ''}
              onChange={(v) => update({ loanFileId: v || undefined })}
              options={loanOptions}
            />
            <Select
              label="Use case"
              value={filters.ucId ?? ''}
              onChange={(v) => update({ ucId: (v || undefined) as FindingsListQuery['ucId'] })}
              options={[
                { value: '', label: 'All' },
                { value: 'uc1', label: 'UC1 — Completeness' },
                { value: 'uc2', label: 'UC2 — Cross-check' },
                { value: 'uc3', label: 'UC3 — CAM recompute' },
              ]}
            />
            <Select
              label="Severity"
              value={filters.severity ?? ''}
              onChange={(v) => update({ severity: (v || undefined) as FindingsListQuery['severity'] })}
              options={[
                { value: '', label: 'All' },
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
                { value: 'info', label: 'Info' },
              ]}
            />
            <Select
              label="Status"
              value={filters.status ?? ''}
              onChange={(v) => update({ status: (v || undefined) as FindingsListQuery['status'] })}
              options={[
                { value: '', label: 'All non-superseded' },
                { value: 'open', label: 'Open' },
                { value: 'accepted', label: 'Accepted' },
                { value: 'waived', label: 'Waived' },
                { value: 'false_positive', label: 'False positive' },
              ]}
            />
            <Select
              label="Verdict source"
              value={filters.verdictSource ?? ''}
              onChange={(v) =>
                update({
                  verdictSource: (v || undefined) as FindingsListQuery['verdictSource'],
                })
              }
              options={[
                { value: '', label: 'All' },
                { value: 'both_agree', label: 'Both paths agree' },
                { value: 'both_disagree', label: 'Paths disagree' },
                { value: 'deterministic', label: 'Rule only' },
                { value: 'llm', label: 'LLM only' },
              ]}
            />
            <Select
              label="Scope"
              value={filters.latestOnly ? 'latest' : 'all'}
              onChange={(v) => update({ latestOnly: v === 'latest' })}
              options={[
                { value: 'latest', label: 'Latest run only' },
                { value: 'all', label: 'All runs' },
              ]}
            />
          </div>
        </Card>

        <DataTable<any>
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(f) => f.id}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          page={filters.page}
          pageSize={filters.pageSize}
          total={data?.total ?? 0}
          onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
          loading={query.isLoading}
          onRowClick={(f) => navigate(`/findings/${f.id}`)}
          emptyMessage="No findings match the current filters."
          groupBy={(f) => ({ key: f.loanFileId, label: f.loanNumber || 'Unknown loan' })}
        />
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Resolve findings?"
        description={`This marks ${resolveScope} as accepted and clears them from this view. This is reversible.`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={resolveAll.isPending}
              onClick={() => {
                resolveAll.mutate();
                setConfirmOpen(false);
              }}
            >
              Resolve all
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          Resolving {resolveScope}.
        </p>
      </Dialog>
    </AppShell>
  );
}

// -------------------------------------------------------------------------
// Local Select primitive — small inline select to avoid pulling another
// dep. Matches the visual language of the other filter controls.
// -------------------------------------------------------------------------

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block">
      <span className="text-micro font-semibold text-on-surface-variant uppercase tracking-wide">
        {label}
      </span>
      <select
        className="w-full mt-1 px-3 py-2 text-label rounded-lg border border-outline-variant bg-surface-container-lowest outline-none focus:border-primary/50"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

void VERDICT_SOURCE;
